import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Select,
  FormControl,
  FormLabel,
  VStack,
  Text,
} from '@chakra-ui/react';
import axios from '../utils/api';
import { buildLockedHierarchySelections, isSuperAdminRole } from '../utils/auth';

const base_url = process.env.REACT_APP_API_URL_ADMIN;

const userHierarchy = [
  { key: 'company_id', label: 'Company', role: 'company' },
  { key: 'admin_id', label: 'Admin', role: 'admin' },
  { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
  { key: 'stokez_id', label: 'Stokez', role: 'stokez' },
  { key: 'agent_id', label: 'Agent', role: 'agent' },
  { key: 'player_id', label: 'Player', role: 'player' },
];

function filledEntries(values = {}) {
  return Object.fromEntries(
    Object.entries(values).filter(([, v]) => v != null && v !== '')
  );
}

const CascadingDropdown = ({ onSelectionChange, initialValues = {}, roleBeingCreated }) => {
  const [selections, setSelections] = useState(() => filledEntries(initialValues));
  const [options, setOptions] = useState({});
  const { user } = useSelector((state) => state.auth);
  const [visibleLevels, setVisibleLevels] = useState([]);
  const lockedNotifiedRef = useRef(false);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  // Re-init selections once user is available (state may hydrate after mount)
  useEffect(() => {
    const userRoleIndex = userHierarchy.findIndex((level) => level.role === user?.role);
    const createdRoleIndex = userHierarchy.findIndex((level) => level.role === roleBeingCreated);

    const startIndex = isSuperAdminRole(user?.role)
      ? 0
      : userRoleIndex >= 0
        ? userRoleIndex + 1
        : 0;
    const endIndex = createdRoleIndex >= 0 ? createdRoleIndex : userHierarchy.length;
    setVisibleLevels(userHierarchy.slice(startIndex, Math.max(startIndex, endIndex)));

    const locked = buildLockedHierarchySelections(user);
    if (!lockedNotifiedRef.current && Object.keys(locked).length > 0) {
      lockedNotifiedRef.current = true;
      setSelections((prev) => {
        const next = { ...locked, ...filledEntries(prev), ...filledEntries(initialValues) };
        onSelectionChangeRef.current?.(next);
        return next;
      });
    }
    // initialValues intentionally omitted — edit sync is handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, roleBeingCreated]);

  const initialFilledKey = JSON.stringify(filledEntries(initialValues));
  useEffect(() => {
    const filled = filledEntries(JSON.parse(initialFilledKey || '{}'));
    if (Object.keys(filled).length === 0) return;
    setSelections((prev) => {
      const changed = Object.keys(filled).some((k) => String(prev[k] || '') !== String(filled[k]));
      return changed ? { ...prev, ...filled } : prev;
    });
  }, [initialFilledKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      if (!visibleLevels.length) {
        if (!cancelled) setOptions({});
        return;
      }

      const updatedOptions = {};
      for (let i = 0; i < visibleLevels.length; i++) {
        const level = visibleLevels[i];
        const parentReady = i === 0 || selections[visibleLevels[i - 1].key];
        if (!parentReady) {
          updatedOptions[level.key] = [];
          continue;
        }

        const filters = {};
        if (i === 0) {
          const levelIdx = userHierarchy.findIndex((l) => l.key === level.key);
          for (let j = 0; j < levelIdx; j++) {
            const parentKey = userHierarchy[j].key;
            if (selections[parentKey]) {
              filters[parentKey] = selections[parentKey];
            }
          }
        } else {
          filters[visibleLevels[i - 1].key] = selections[visibleLevels[i - 1].key];
        }

        try {
          const response = await axios.post(`${base_url}`, {
            apiName: 'user',
            command: 'getOptions',
            level: level.key,
            filters,
          });
          updatedOptions[level.key] = Array.isArray(response.data?.options)
            ? response.data.options
            : [];
        } catch (error) {
          console.error(`Error fetching options for ${level.key}:`, error);
          updatedOptions[level.key] = [];
        }
      }

      if (!cancelled) setOptions(updatedOptions);
    };

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [selections, visibleLevels]);

  const handleChange = (level, value) => {
    const updatedSelections = { ...selections, [level]: value };
    const levelIndex = visibleLevels.findIndex((l) => l.key === level);
    visibleLevels.slice(levelIndex + 1).forEach((l) => {
      updatedSelections[l.key] = '';
    });
    setSelections(updatedSelections);
    onSelectionChange?.(updatedSelections);
  };

  if (!visibleLevels.length) {
    return (
      <Text fontSize="sm" color="gray.600">
        Company and parent accounts are set from your login session.
      </Text>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {visibleLevels.map((level) => {
        const levelIndex = visibleLevels.findIndex((l) => l.key === level.key);
        const parentKey = levelIndex > 0 ? visibleLevels[levelIndex - 1].key : null;
        return (
          <FormControl key={level.key}>
            <FormLabel htmlFor={level.key}>{level.label}</FormLabel>
            <Select
              id={level.key}
              placeholder={`Select ${level.label}`}
              value={selections[level.key] || ''}
              onChange={(e) => handleChange(level.key, e.target.value)}
              isDisabled={levelIndex > 0 && !selections[parentKey]}
            >
              {(options[level.key] || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        );
      })}
    </VStack>
  );
};

export default CascadingDropdown;
